import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, input, output, signal, WritableSignal, inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Params, RouterModule } from '@angular/router';

interface Ripple {
  LocalId: number,
  ButtonXLocation: number,
  ButtonYLocation: number,
  /**
   * In unix milliseconds, when this was created
   * I.e., when the mouse/touch started that created this ripple effect
   */
  CreationTime: number,
  IsAnimationDone: boolean
}

@Component({
  selector: 'app-button',
  imports: [NgTemplateOutlet, RouterModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[class.disabled]": "Disabled()",
    "(click)": "HostClickEvent.emit($event)"
  }
})
export class ButtonComponent implements OnInit, OnDestroy {
  public readonly ElementRef = inject(ElementRef);

  public Href = input<string | undefined>(undefined);
  /**
   * If provided, provide in the following format:
   * {'query-param-name': 'some-value'}
   */
  public QueryParams = input<Params | undefined>(undefined);
  public Target = input<"_blank" | undefined>(undefined);
  public ContentAlignment = input<"start" | "center" | "end">("center");
  public FullWidth = input<boolean>(false);
  public FullHeight = input<boolean>(false);
  /**
   * Only valid when Href() is provided and IsRouterLink is false.
   */
  public IsDownload = input<boolean>(false);
  public IsRouterLink = input<boolean>(true);
  public ActiveClass = input<string>("");
  public ActiveExact = input<boolean>(false);
  public IsRippleDark = input<boolean>(false);
  public IsOutlined = input<boolean>(false);
  public IsLabel = input<boolean>(false);
  public IsRaised = input<boolean>(false);
  public For = input<string>("");
  /**
   * If there is no Href and this is a button meant to submit a form, the Id
   * of the form should be provided (without a preceeding # character).
   */
  public FormId = input<string | undefined>();
  public Variant = input<"primary" | "danger" | "cancel" | "gray">("primary");
  public IsNoBackground = input<boolean>(false);
  public Size = input<"small" | "medium" | "large">("medium");
  public Disabled = input<boolean>(false);
  public NextRippleLocalId = 1;

  public RippleStartPageX: WritableSignal<number> = signal(0);
  public RippleStartPageY: WritableSignal<number> = signal(0);
  public RippleSize: WritableSignal<number> = signal(200);
  public IsInputDeviceHoldingButton: WritableSignal<boolean> = signal(false);
  public WindowMouseUpCallback: () => void;
  public WindowTouchEndCallback: () => void;
  /**
   * An array of ripple effects
   */
  public Ripples: WritableSignal<Ripple[]> = signal([]);
  public LastInputDeviceDownTime: WritableSignal<number> = signal(0);

  /**
   * Use this when you need to programmatically listen for this component's click event.
   * For most circumstances, don't use this and just use the (click) binding in the HTML.
   */
  public HostClickEvent = output<Event>();

  /**
   * The amount of milliseconds to ignore quick-fire click events for this button.
   */
  private ClickEventTimeout = 100;
  private LastClickEventTime = 0;

  public constructor() {
    this.WindowMouseUpCallback = () => {
      this.IsInputDeviceHoldingButton.set(false);
      this.ClearStaleRipples();
    }

    this.WindowTouchEndCallback = () => {
      this.IsInputDeviceHoldingButton.set(false);
      this.ClearStaleRipples();
    }
  }

  public ngOnInit(): void {
    window.addEventListener("mouseup", this.WindowMouseUpCallback, { passive: true });
    window.addEventListener("touchend", this.WindowTouchEndCallback, { passive: true });
  }

  public ngOnDestroy(): void {
    window.removeEventListener("mouseup", this.WindowMouseUpCallback);
    window.removeEventListener("touchend", this.WindowTouchEndCallback);
  }

  public OnMouseDown(e: MouseEvent): void {
    this.StartRipple((e.target as HTMLElement).offsetParent as HTMLElement, e.clientX, e.clientY);
    this.LastInputDeviceDownTime.set(new Date().getTime());
    this.IsInputDeviceHoldingButton.set(true);
  }

  public OnTouchStart(e: TouchEvent): void {
    this.StartRipple((e.target as HTMLElement).offsetParent as HTMLElement, e.touches[0].clientX, e.touches[0].clientY);
    this.LastInputDeviceDownTime.set(new Date().getTime());
    this.IsInputDeviceHoldingButton.set(true);
  }

  private StartRipple(offsetParent: HTMLElement, x: number, y: number): void {
    const buttonElement = (this.ElementRef.nativeElement as HTMLElement).firstElementChild;
    const buttonBoundingRect = buttonElement!.getBoundingClientRect();
    const rippleStartX = x - buttonBoundingRect.left;
    const rippleStartY = y - buttonBoundingRect.top;
    this.RippleSize.set(buttonBoundingRect.width * 2);
    this.Ripples.update(cur => [...cur, {
      LocalId: this.NextRippleLocalId++,
      ButtonXLocation: rippleStartX,
      ButtonYLocation: rippleStartY,
      CreationTime: (new Date()).getTime(),
      IsAnimationDone: false
    }]);
  }

  public OnAnimationEnd(e: AnimationEvent, ripple: Ripple, index: number): void {
    if (e.animationName.endsWith("button-ripple-grow")) {
      ripple.IsAnimationDone = true;
      if (this.IsInputDeviceHoldingButton()) {
        if (this.LastInputDeviceDownTime() === ripple.CreationTime) {
          // Leave it alive
        } else {
          // Splice this one out
          this.Ripples.update(cur => {
            cur.splice(index, 1);
            return cur;
          });
        }
      } else {
        // Splice this one out
        this.Ripples.update(cur => {
          cur.splice(index, 1);
          return cur;
        });
      }
    }
  }

  /**
   * Clear any ripples which have their animation's finished.
   * This is called when the input device releases on the button.
   */
  public ClearStaleRipples(): void {
    const newRipplesArray: Ripple[] = [];
    for (const ripple of this.Ripples().values()) {
      if (!ripple.IsAnimationDone) {
        newRipplesArray.push(ripple);
      }
      this.Ripples.set(newRipplesArray);
    }
  }
}